// Boss card styled as a Magic: The Gathering card (v0.8.9).
//
// Layout:
//   1. Outer gold/black frame (the "card border") with a subtle
//      inner dark frame so it reads as a double-border.
//   2. Title spine: the boss name set vertically down the left
//      edge in a narrow gold-toned strip, with the rarity pip
//      above it. The name reads top to bottom, book-spine style.
//   3. Right column, top to bottom:
//        a. Art box: the creature (3D canvas or ASCII sprite),
//           tinted with a coloured vignette from the boss's
//           first challenger colour so each card has a distinct
//           "land".
//        b. Type line: thin dark strip, "Creature" or
//           "Creature — Legendary / Mythical".
//        c. Stat block: two columns. Left: HP bar + remaining /
//           max HP. Right: damage dealt + tickets closed (X/Y).
//           Mirrors MTG's P/T strip at the bottom right.
//        d. Challenger list (full variant).
//
// The card itself keeps the per-instance WebGL fallback from
// the previous version — the 3D creature canvas falls back to
// the ASCII sprite per-card if WebGL is unavailable.

import { useState } from 'react';
import type { SpriteDef } from './sprites';
import { TOMBSTONE_SPRITE } from './sprites';
import { CreatureCanvas } from './CreatureCanvas';

export type BossStatus = 'active' | 'vanquished' | 'survived';
export type RenderEngine = 'ascii' | '3d';

export interface BossCardMock {
  id: string;
  name: string;
  hp: number;
  damage_dealt: number;
  status: BossStatus;
  ends_at: string;
  tickets_total?: number;
  tickets_closed?: number;
  users: Array<{ user_id: string; handle: string; name: string | null; damage: number; color?: string | null }>;
}

interface Props {
  boss: BossCardMock;
  sprite: SpriteDef;
  variant?: 'full' | 'compact';
  size?: 'sm' | 'md' | 'lg';
  engine?: RenderEngine;
  myDamage?: number;
}

// Three.js canvas: the art box always paints to this size
// regardless of card size. Keeps the creature recognisable.
const ART_PX = 160;

// Boss rarity tier derived from max HP. Drives the pip on the
// title spine and the type line.
//
//   hp < 10       -> ''          (pip C, common — type line just
//                                 reads 'Creature')
//   hp 10..19     -> 'Legendary' (pip R, rare)
//   hp >= 20      -> 'Mythical'  (pip M, mythic)
//
// Operator picked these thresholds for visual variety on the
// wall display: AMER v1.8 (14) is Legendary, the bigger
// AMER/AMER/APAC bosses (~29) are Mythical, a future
// small-scope boss is just a plain Creature. HP is the boss's
// "weight" so it doubles as rarity.
//
// v0.8.9: the word 'Boss' was dropped from the label — the type
// line is 'Creature' or 'Creature — Legendary', not
// 'Creature — Legendary Boss'.
type BossTier = { label: string; pip: string };
function bossTier(maxHp: number): BossTier {
  if (maxHp >= 20) return { label: 'Mythical', pip: 'M' };
  if (maxHp >= 10) return { label: 'Legendary', pip: 'R' };
  return { label: '', pip: 'C' };
}

// Derive a hue-shifted colour for the art vignette from the
// boss's first user chip color (fallback: fantasy-gold).
function deriveAccent(hex: string | null | undefined): string {
  if (!hex) return '#c9a14a';
  return hex;
}

// RGB hex -> 'r, g, b' string for use in rgba() so the
// vignette can be applied with varying opacity.
function hexToRgb(hex: string): string {
  const m = hex.replace('#', '');
  const r = parseInt(m.slice(0, 2), 16);
  const g = parseInt(m.slice(2, 4), 16);
  const b = parseInt(m.slice(4, 6), 16);
  return `${r}, ${g}, ${b}`;
}

export function BossCard({ boss, sprite, variant = 'full', size = 'md', engine = 'ascii', myDamage }: Props) {
  // Two sizes now: sm (carousel tile) and lg (boss-fights page).
  // Cards are taller-than-wide-ish to match MTG's 63:88 ratio,
  // but we don't enforce strict aspect so the creature canvas
  // gets enough vertical room.
  const sizeCls = {
    sm: 'w-56 text-xs',
    md: 'w-64 text-sm',
    lg: 'w-72 text-base',
  }[size];

  const remaining = Math.max(0, boss.hp - boss.damage_dealt);
  const pct = boss.hp > 0 ? Math.min(100, Math.round((remaining / boss.hp) * 100)) : 0;
  const vanquished = boss.status === 'vanquished';
  const survived = boss.status === 'survived';
  const inactive = vanquished || survived;

  const [fallback, setFallback] = useState(false);
  const activeEngine: RenderEngine = engine === '3d' && !fallback ? '3d' : 'ascii';
  // v0.8.9: vanquished bosses swap their creature for a tombstone
  // sprite in the ASCII engine. The 3D engine has its own GL layer
  // (CreatureCanvas) that doesn't expose a swap path cleanly, and
  // Pi 4 can drop WebGL entirely on the boss-fights page already —
  // so the tombstone fallback only matters here. When engine='3d'
  // falls back to ASCII (onFallback), this branch picks the
  // tombstone.
  const effectiveSprite: SpriteDef =
    boss.status === 'vanquished' && activeEngine === 'ascii'
      ? TOMBSTONE_SPRITE
      : sprite;

  const topUsers = boss.users.slice(0, 3);
  const overflow = Math.max(0, boss.users.length - topUsers.length);

  // Rarity tier derived from max HP — see bossTier() below.
  const tier = bossTier(boss.hp);

  // Frame colour: gold when vanquished (trophy), copper/amber
  // when survived, red when active. Matches the boss-card border
  // colour convention from the previous design.
  const frameCls = vanquished
    ? 'border-gold/90'
    : survived
      ? 'border-amber-500/80'
      : 'border-red-600/70';

  // Accent for the art vignette: use the first chip color if
  // available, else fantasy-gold. Gives each boss its own tint.
  const accent = deriveAccent(topUsers[0]?.color ?? null);
  const accentRgb = hexToRgb(accent);
  const artBg = `radial-gradient(ellipse at center top, rgba(${accentRgb}, 0.55) 0%, rgba(${accentRgb}, 0.18) 45%, rgba(0,0,0,0.85) 100%)`;

  return (
    // OUTER FRAME: thick double border (gold outer, dark inner).
    // The outer 1px ring is the gold bevel; the 6px border is the
    // outer frame; the 1px inside it is the inner dark line.
    <div
      className={`relative ${sizeCls} rounded-xl border-[3px] ${frameCls} bg-gradient-to-br from-zinc-900 via-black to-zinc-900 shadow-[0_4px_14px_rgba(0,0,0,0.6),0_0_0_1px_rgba(0,0,0,0.9)_inset] [transform:rotate(-0.4deg)] hover:[transform:rotate(0deg)] transition-transform`}
    >
      {/* Vanquished gold shimmer (CSS keyframe overlay) */}
      {vanquished && <div className="vanquished-flash pointer-events-none absolute inset-0 rounded-xl" />}

      {/* Inner dark frame line (1px) so the border reads as double */}
      <div className="pointer-events-none absolute inset-[5px] rounded-lg border border-black/80" />

      {/* Body: vertical title spine on the left, content column on
          the right. align-items defaults to stretch, so the spine
          runs the full height of the card. */}
      <div className="relative flex gap-1.5 p-2">
        {/* TITLE SPINE: rarity pip on top, boss name running
            vertically down. writing-mode: vertical-rl flows the
            text top-to-bottom with the glyphs rotated 90° — a book
            spine. whitespace-nowrap + ellipsis clips an over-long
            name along the vertical axis. */}
        <div className="flex w-7 shrink-0 flex-col items-center gap-2 overflow-hidden rounded-md border border-gold/40 bg-gradient-to-b from-zinc-800 to-black py-2">
          <span
            className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-gold/70 bg-black text-[10px] font-bold text-gold"
            title={tier.label || 'Boss'}
          >
            {tier.pip}
          </span>
          <span
            className={`min-h-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-center font-bold tracking-wide ${
              vanquished ? 'text-gold' : 'text-amber-100'
            }`}
            style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
            title={boss.name}
          >
            {boss.name}
          </span>
        </div>

        {/* CONTENT COLUMN */}
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          {/* ART BOX: the creature canvas with a coloured vignette
              behind it. The vignette tints the "land" the creature
              stands on; falling back to ASCII still gets the same
              art-frame treatment so the cards look consistent. */}
          <div className="relative overflow-hidden rounded-md border border-black/70" style={{ background: artBg }}>
            {/* Subtle inner shadow ring so the art looks recessed */}
            <div className="pointer-events-none absolute inset-0 rounded-md shadow-[inset_0_0_18px_rgba(0,0,0,0.7)]" />
            <div className="flex items-center justify-center" style={{ height: ART_PX }}>
              <div
                className={`relative flex items-center justify-center ${
                  activeEngine === '3d'
                    ? ''
                    : `font-mono leading-[0.95] ${effectiveSprite.accent} ${vanquished ? 'sprite-vanquished' : survived ? 'sprite-slumped' : 'sprite-bob'}`
                }`}
                style={
                  activeEngine === '3d'
                    ? { width: ART_PX, height: ART_PX }
                    : { fontSize: '14px' }
                }
              >
                {activeEngine === '3d' ? (
                  <CreatureCanvas bossName={boss.name} status={boss.status} onFallback={() => setFallback(true)} />
                ) : (
                  <>
                    <pre className="m-0 whitespace-pre">{effectiveSprite.lines.join('\n')}</pre>
                    {vanquished && (
                      <>
                        <pre className="vanquished-glint-a pointer-events-none absolute inset-0 m-0 whitespace-pre text-gold/30">
                          {effectiveSprite.lines.join('\n')}
                        </pre>
                        <pre className="vanquished-glint-b pointer-events-none absolute inset-0 m-0 whitespace-pre text-gold/20">
                          {effectiveSprite.lines.join('\n')}
                        </pre>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* TYPE LINE: small text strip below the art. Just
              'Creature' at the base tier, 'Creature — Legendary'
              / 'Creature — Mythical' above it. The set
              abbreviation that used to sit on the right (MRB) was
              dropped at the operator's request. */}
          <div className="flex items-center justify-between border-b border-gold/30 px-2 pb-1 text-[10px] uppercase tracking-[0.18em] text-amber-200/80">
            <span>Creature{tier.label ? ` — ${tier.label}` : ''}</span>
          </div>

          {/* STAT BLOCK: HP bar (left) + tickets/damage numbers
              (right). Like an MTG P/T box but with HP semantics. */}
          <div className="grid grid-cols-2 gap-2 px-1 pb-1">
            {/* Left column: HP bar */}
            <div>
              <div className="flex items-baseline justify-between text-[10px] uppercase tracking-wide text-amber-200/70">
                <span>HP</span>
                <span className="tabular-nums text-ink">
                  {remaining}/{boss.hp}
                </span>
              </div>
              <div className="mt-0.5 rounded-full border border-white/10 bg-black/40 p-[2px] shadow-inner">
                <div
                  className={`relative h-2 rounded-full overflow-hidden ${
                    vanquished
                      ? 'bg-gradient-to-b from-gold to-black'
                      : survived
                        ? 'bg-gradient-to-b from-amber-300 to-amber-700'
                        : 'bg-gradient-to-b from-red-400 to-red-700'
                  }`}
                  style={{ width: `${vanquished ? 100 : pct}%` }}
                >
                  {!inactive && (
                    <div className="pointer-events-none absolute inset-x-0 top-0 h-1/2 rounded-full bg-gradient-to-b from-white/35 to-transparent" />
                  )}
                </div>
              </div>
            </div>

            {/* Right column: damage dealt + tickets closed */}
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wide text-amber-200/70">Dmg</div>
              <div className="tabular-nums text-base font-bold leading-tight text-amber-100">{boss.damage_dealt}</div>
              {boss.tickets_total !== undefined && boss.tickets_closed !== undefined && (
                <div
                  className={`tabular-nums text-[10px] uppercase tracking-wide ${
                    boss.tickets_closed === boss.tickets_total && !vanquished && !survived
                      ? 'text-gold font-bold'
                      : 'text-ink-dim'
                  }`}
                  title="tickets closed within the boss window"
                >
                  {boss.tickets_closed}/{boss.tickets_total} closed
                </div>
              )}
            </div>
          </div>

          {/* PARTY LIST (full variant only) — MTG-style description box.
              Dark recessed panel with the challengers, max 3 + overflow. */}
          {variant === 'full' && (
            <div className="rounded-md border border-black/60 bg-gradient-to-b from-black/70 to-black/40 p-2 shadow-inner">
              {myDamage !== undefined && (
                <div className="flex items-center justify-between text-[10px] uppercase tracking-wide">
                  <span className="text-amber-200/70">Your damage</span>
                  <span className="tabular-nums font-semibold text-red-300">{myDamage}</span>
                </div>
              )}
              <div className="text-[10px] uppercase tracking-[0.18em] text-amber-200/70 mt-1">
                {boss.users.length === 0 ? 'No challengers yet' : '⚔ Challengers'}
              </div>
              {topUsers.map((u) => (
                <div key={u.user_id} className="mt-0.5 flex items-center justify-between gap-2 text-[11px]">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span
                      className="h-2.5 w-2.5 rounded-full ring-1 ring-black/60 flex-shrink-0"
                      style={{ background: u.color ?? '#666' }}
                    />
                    <span className="truncate text-ink">{u.name ?? u.handle}</span>
                  </div>
                  <span className="tabular-nums text-amber-100 whitespace-nowrap">{u.damage}</span>
                </div>
              ))}
              {overflow > 0 && (
                <div className="text-[10px] text-ink-dim mt-0.5">+ {overflow} more</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
